import moment from "moment";

import type { AccountSubType } from "~/v2/api/rest/account";

/**
 * Whether an option applies at, below, or regardless of the 59½ age threshold.
 */
export type AgeRule = "over59" | "under59" | "either";

export interface IraDistributionReason {
  // Value sent to the backend as `ira.distribution_reason`.
  distributionReason: string;
  /** Account type this reason applies to. */
  accountSubType: AccountSubType;
  /** Age eligibility relative to the 59½ threshold. */
  ageRule: AgeRule;
  /** Plain-English option shown in the dropdown (IRA Distribution Copy Table). */
  label: string;
  /** Helper text shown for the selected reason (IRA Distribution Copy Table). */
  helperText: string;
}

// IRA is US only, so we can hardcode tax years to match calendar year
const CURRENT_YEAR = new Date().getFullYear().toString();
const PREVIOUS_YEAR = (new Date().getFullYear() - 1).toString();

export const IRA_DISTRIBUTION_REASONS: readonly IraDistributionReason[] = [
  {
    distributionReason: "normal",
    accountSubType: "traditional",
    ageRule: "over59",
    label: "Normal Distribution",
    helperText: "Standard IRA withdrawal, age 59½ or older",
  },
  {
    distributionReason: "excess_removal_current_year",
    accountSubType: "traditional",
    ageRule: "over59",
    label: `Over-Contribution removal ${CURRENT_YEAR}`,
    helperText: `Remove excess IRA contribution and earnings ${CURRENT_YEAR}`,
  },
  {
    distributionReason: "excess_removal_1_year_prior_over_59.5",
    accountSubType: "traditional",
    ageRule: "over59",
    label: `Over-Contribution removal ${PREVIOUS_YEAR}`,
    helperText: `Remove excess IRA contribution and earnings ${PREVIOUS_YEAR}`,
  },
  {
    distributionReason: "early_no_exception",
    accountSubType: "traditional",
    ageRule: "under59",
    label: "Early Distribution",
    helperText:
      "Early distribution, no known exception, additional tax penalties may apply",
  },
  {
    distributionReason: "excess_removal_current_year_under_59.5",
    accountSubType: "traditional",
    ageRule: "under59",
    label: `Over-Contribution Correction ${CURRENT_YEAR}`,
    helperText: `Remove excess IRA contribution and earnings ${CURRENT_YEAR}`,
  },
  {
    distributionReason: "excess_removal_1_year_prior_under_59.5",
    accountSubType: "traditional",
    ageRule: "under59",
    label: `Over-Contribution Correction ${PREVIOUS_YEAR}`,
    helperText: `Remove excess IRA contribution and earnings for ${PREVIOUS_YEAR}`,
  },
  {
    distributionReason: "normal_roth",
    accountSubType: "roth",
    ageRule: "over59",
    label: "Qualified Distribution",
    helperText: "Standard IRA withdrawal, age 59½ or older",
  },
  {
    distributionReason: "early_roth_no_exception",
    accountSubType: "roth",
    ageRule: "under59",
    label: "Early Distribution",
    helperText:
      "Early distribution, no known exception, additional tax penalties may apply",
  },
  {
    distributionReason: "excess_removal_roth",
    accountSubType: "roth",
    ageRule: "under59",
    label: `Over-Contribution removal ${CURRENT_YEAR}`,
    helperText: `Remove excess IRA contribution and earnings ${CURRENT_YEAR}`,
  },
  {
    distributionReason: "excess_removal_1_year_prior_roth",
    accountSubType: "roth",
    ageRule: "under59",
    label: `Over-Contribution Correction ${PREVIOUS_YEAR}`,
    helperText: `Remove excess IRA contribution and earnings for ${PREVIOUS_YEAR}`,
  },
];

/**
 * Returns true when the owner is at least 59½ years old as of `now`.
 *
 * Returns false for missing/invalid dates of birth.
 */
export const getIsAtLeast59Half = (
  dateOfBirth?: string | null,
  now: moment.Moment = moment(),
): boolean => {
  if (!dateOfBirth) {
    return false;
  }

  const dob = moment(dateOfBirth);
  if (!dob.isValid()) {
    return false;
  }

  const fiftyNineAndAHalf = dob.clone().add(59, "years").add(6, "months");
  return now.isSameOrAfter(fiftyNineAndAHalf);
};

/**
 * Filters the distribution reasons down to those valid for the given account
 * sub-type and age, per the IRA Distribution Copy Table.
 */
export const getEligibleDistributionReasons = ({
  accountSubType,
  isOver59Half,
}: {
  accountSubType?: AccountSubType;
  isOver59Half: boolean;
}): IraDistributionReason[] => {
  if (!accountSubType) {
    return [];
  }

  return IRA_DISTRIBUTION_REASONS.filter((reason) => {
    if (reason.accountSubType !== accountSubType) {
      return false;
    }

    if (reason.ageRule === "over59") {
      return isOver59Half;
    }

    if (reason.ageRule === "under59") {
      return !isOver59Half;
    }

    return true;
  });
};
